"use client";

import Link from "next/link";
import { useUserSession } from "@/lib/context/user-session-context";
import { User, LogOut, ShoppingBag } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { accountsEnabled } from "@/lib/feature-flags";

/**
 * Right-side navbar widget: shows "Sign in" when signed out, or a
 * "Welcome back <first-name>" button with a dropdown when signed in.
 * Dropdown links to /account, /account/orders, and sign out.
 */
export default function UserNavWidget() {
  // Accounts still WIP — hidden entirely unless the flag is on for this env.
  if (!accountsEnabled()) return null;
  const { user, loading, logout } = useUserSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (loading) return <div className="w-8 h-8" aria-hidden="true" />;

  if (!user) {
    return (
      <Link
        href="/account"
        aria-label="Sign in"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-primary_green whitespace-nowrap"
      >
        <User size={20} strokeWidth={2.25} />
        {/* Text only when there's real room, hidden on tight desktop widths */}
        <span className="hidden 2xl:inline">Sign in</span>
      </Link>
    );
  }

  const firstName = (user.name || "").split(" ")[0] || user.name;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={`Account: ${firstName}`}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-800 hover:text-primary_green whitespace-nowrap"
      >
        <div className="w-7 h-7 rounded-full bg-primary_green text-white text-xs font-bold flex items-center justify-center shrink-0">
          {firstName.slice(0, 1).toUpperCase()}
        </div>
        <span className="hidden 2xl:inline">Hi, {firstName}</span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs uppercase tracking-widest text-primary_green font-semibold">Signed in as</p>
            <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
            <p className="text-xs text-gray-500 truncate">{user.email}</p>
          </div>
          <Link
            href="/account"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <User size={14} className="text-gray-400" />
            My account
          </Link>
          <Link
            href="/account/orders"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <ShoppingBag size={14} className="text-gray-400" />
            My orders
          </Link>
          <button
            onClick={async () => { setOpen(false); await logout(); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
